import { useCallback, useSyncExternalStore } from 'react';

import { MAX_WORD_LENGTH, normaliseWord } from './spell-check-protocol';

/**
 * The person's own spelling dictionary, kept in this browser (no server storage). Words are stored per
 * signed-in person so someone else signing in on the same browser does not reuse them.
 */
export const PERSONAL_DICTIONARY_PREFIX = 'managed-document:personal-dictionary:';
export const MAX_PERSONAL_WORDS = 1000;

const WORD_SHAPE = /^[\p{L}\p{M}]+(?:['\u2019-][\p{L}\p{M}]+)*$/u;
const EMPTY: readonly string[] = Object.freeze([]);

/** The stored form of a word: curly apostrophes straightened, lower case, trimmed. */
export function personalWordKey(word: string): string {
  return normaliseWord(word.trim()).toLowerCase();
}

/** Why a word cannot be added, or null when it can. */
export function personalWordProblem(word: string, existing: readonly string[] = []): string | null {
  const key = personalWordKey(word);
  if (!key) return 'Enter a word.';
  if (key.length > MAX_WORD_LENGTH) return `Words can be up to ${MAX_WORD_LENGTH} characters.`;
  if (!WORD_SHAPE.test(key)) return 'Use letters only, with apostrophes or hyphens inside the word.';
  if (existing.includes(key)) return 'This word is already in your dictionary.';
  if (existing.length >= MAX_PERSONAL_WORDS) return `Your dictionary can hold up to ${MAX_PERSONAL_WORDS} words. Remove some first.`;
  return null;
}

const listeners = new Set<() => void>();
const cache = new Map<string, readonly string[]>();
const memory = new Map<string, readonly string[]>();

function storageKey(scope: string): string { return PERSONAL_DICTIONARY_PREFIX + scope.trim().toLowerCase(); }

function parse(raw: string | null): readonly string[] {
  if (!raw) return EMPTY;
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return EMPTY;
    const words = new Set<string>();
    for (const item of value) {
      if (typeof item !== 'string') continue;
      const key = personalWordKey(item);
      if (key && key.length <= MAX_WORD_LENGTH && WORD_SHAPE.test(key)) words.add(key);
      if (words.size >= MAX_PERSONAL_WORDS) break;
    }
    return Object.freeze([...words].sort((a, b) => a.localeCompare(b)));
  } catch {
    return EMPTY;
  }
}

/** The person's words in alphabetical order. */
export function readPersonalWords(scope: string): readonly string[] {
  const key = storageKey(scope);
  const remembered = memory.get(key);
  if (remembered) return remembered;
  let raw: string | null = null;
  try { raw = window.localStorage.getItem(key); } catch { raw = null; }
  const cached = cache.get(key);
  if (cached && JSON.stringify(cached) === (raw ?? '[]')) return cached;
  const words = parse(raw);
  cache.set(key, words);
  return words;
}

function write(scope: string, words: readonly string[]): void {
  const key = storageKey(scope);
  const sorted = Object.freeze([...new Set(words)].sort((a, b) => a.localeCompare(b)));
  try {
    window.localStorage.setItem(key, JSON.stringify(sorted));
    memory.delete(key);
    cache.set(key, sorted);
  } catch {
    // Storage can be unavailable (private browsing); the words still apply to this page.
    memory.set(key, sorted);
  }
  for (const listener of listeners) listener();
}

export type PersonalWordResult = { ok: true; word: string } | { ok: false; message: string };

export function addPersonalWord(scope: string, word: string): PersonalWordResult {
  const current = readPersonalWords(scope);
  const problem = personalWordProblem(word, current);
  if (problem) return { ok: false, message: problem };
  const key = personalWordKey(word);
  write(scope, [...current, key]);
  return { ok: true, word: key };
}

export function removePersonalWord(scope: string, word: string): void {
  const current = readPersonalWords(scope);
  const key = personalWordKey(word);
  if (!current.includes(key)) return;
  write(scope, current.filter(item => item !== key));
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => { if (event.key?.startsWith(PERSONAL_DICTIONARY_PREFIX)) listener(); };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export interface PersonalDictionary {
  words: readonly string[];
  add: (word: string) => PersonalWordResult;
  remove: (word: string) => void;
}

/** The signed-in person's dictionary; also kept in step across tabs. */
export function usePersonalDictionary(scope: string): PersonalDictionary {
  const words = useSyncExternalStore(subscribe, () => readPersonalWords(scope), () => EMPTY);
  const add = useCallback((word: string) => addPersonalWord(scope, word), [scope]);
  const remove = useCallback((word: string) => removePersonalWord(scope, word), [scope]);
  return { words, add, remove };
}
